import React from 'react';
import { Box, Container, Typography, Button, Chip, Avatar } from '@mui/material';
import { FaGithub, FaLinkedin, FaEnvelope, FaFileAlt, FaChevronDown } from 'react-icons/fa';
import { motion } from 'framer-motion';
import profile from '../data/profile';
import { trackCVDownload, trackSocialClick } from '../utils/analytics';

function HeroSection() {
  const { name, title, subtitle, tagline, links, status } = profile;

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(160deg, #0D0C0B 0%, #161310 45%, #1E1812 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Warm glow top-right */}
      <Box sx={{ position: 'absolute', top: '-15%', right: '-8%', width: '45%', height: '65%', background: 'radial-gradient(circle, rgba(196,120,93,0.09) 0%, transparent 68%)', borderRadius: '50%', pointerEvents: 'none' }} />
      {/* Warm glow bottom-left */}
      <Box sx={{ position: 'absolute', bottom: '-8%', left: '-4%', width: '38%', height: '45%', background: 'radial-gradient(circle, rgba(196,120,93,0.05) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 5, md: 10 },
          }}
        >
          {/* Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Box sx={{ position: 'relative', width: { xs: 200, sm: 260, md: 320 }, height: { xs: 200, sm: 260, md: 320 } }}>
              <Box
                sx={{
                  position: 'absolute',
                  width: { xs: 238, sm: 304, md: 370 },
                  height: { xs: 238, sm: 304, md: 370 },
                  borderRadius: '50%',
                  border: '1.5px solid rgba(196,120,93,0.22)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none',
                }}
              />
              <Avatar
                src="/myimages/profile/image1.jpg"
                alt={name}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '3px solid rgba(196,120,93,0.2)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                  '& img': { objectPosition: 'center 20%' },
                }}
              />
            </Box>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' }, color: '#EAE3D6' }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{ fontWeight: 400, mb: 0.75, fontSize: { xs: '2.8rem', sm: '3.6rem', md: '5rem' }, color: '#EAE3D6', lineHeight: 1.1 }}
              >
                {name}
              </Typography>

              <Typography
                sx={{ fontWeight: 400, mb: 0.5, fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.75rem' }, color: 'rgba(234,227,214,0.72)', fontFamily: "'DM Sans', sans-serif" }}
              >
                {title}
              </Typography>

              <Typography
                sx={{ mb: { xs: 1.5, md: 2 }, color: 'rgba(234,227,214,0.48)', fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } }}
              >
                {subtitle}
              </Typography>

              {tagline && (
                <Typography
                  sx={{ mb: 2.5, fontStyle: 'italic', color: 'rgba(234,227,214,0.7)', fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem' }}
                >
                  "{tagline}"
                </Typography>
              )}

              {status && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                  <Chip
                    label={status}
                    sx={{
                      mb: 2.5,
                      backgroundColor: 'rgba(196,120,93,0.15)',
                      color: 'rgba(234,227,214,0.8)',
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', sm: '0.97rem' },
                      border: '1px solid rgba(196,120,93,0.22)',
                      height: 'auto',
                      maxWidth: '100%',
                      py: 1,
                      '& .MuiChip-label': { px: { xs: 1.75, md: 2.25 }, whiteSpace: 'normal' },
                    }}
                  />
                </motion.div>
              )}

              {/* 4 action buttons — one row on desktop, 2×2 on mobile */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' },
                  justifyContent: { xs: 'stretch', sm: 'flex-start' },
                  gap: { xs: 0.9, md: 1 },
                }}
              >
                {links.github && (
                  <Button
                    variant="contained"
                    href={links.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick('GitHub')}
                    startIcon={<FaGithub />}
                    sx={ghostBtnSx}
                  >
                    GitHub
                  </Button>
                )}
                {links.linkedin && (
                  <Button
                    variant="contained"
                    href={links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackSocialClick('LinkedIn')}
                    startIcon={<FaLinkedin />}
                    sx={ghostBtnSx}
                  >
                    LinkedIn
                  </Button>
                )}
                {links.email && (
                  <Button
                    variant="contained"
                    href={`mailto:${links.email}`}
                    onClick={() => trackSocialClick('Email')}
                    startIcon={<FaEnvelope />}
                    sx={ghostBtnSx}
                  >
                    Email
                  </Button>
                )}

                {links.cv && (
                  <Button
                    variant="contained"
                    href={links.cv}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackCVDownload()}
                    startIcon={<FaFileAlt />}
                    sx={{
                      ...ghostBtnSx,
                      backgroundColor: 'rgba(196,120,93,0.18)',
                      color: '#D4937A',
                      border: '1px solid rgba(196,120,93,0.28)',
                      '&:hover': { backgroundColor: 'rgba(196,120,93,0.28)', border: '1px solid rgba(196,120,93,0.4)' },
                    }}
                  >
                    CV
                  </Button>
                )}
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Container>

      {/* Scroll indicator */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        sx={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        {[0, 0.18, 0.36].map((delay, i) => (
          <motion.div
            key={i}
            animate={{ opacity: [0.1, 0.55, 0.1] }}
            transition={{ repeat: Infinity, duration: 1.5, delay, ease: 'easeInOut' }}
          >
            <FaChevronDown size={13} color="rgba(234,227,214,0.45)" />
          </motion.div>
        ))}
      </Box>
    </Box>
  );
}

const ghostBtnSx = {
  backgroundColor: 'rgba(234,227,214,0.07)',
  color: 'rgba(234,227,214,0.8)',
  border: '1px solid rgba(234,227,214,0.11)',
  fontSize: { xs: '0.95rem', md: '1rem' },
  py: { xs: 1.3, md: 1.1 },
  px: { xs: 2, md: 2.25 },
  '&:hover': { backgroundColor: 'rgba(234,227,214,0.13)', border: '1px solid rgba(234,227,214,0.2)' },
};

export default HeroSection;
